import { useCallback, useEffect, useState } from "react";
import { DocumentReviewProjection } from "@/types";
import { getPartnerAwareApiUrl, httpClient } from "@/providers/dataProvider/dataProvider";
import { projectionErrorReason } from "../utils/projectionError";

interface UseReviewProjectionProps {
  documentId: string | undefined;
  /** Resolved client UUID. Projection is scoped by client — matching is client-scoped. */
  clientId: string | undefined;
  /** Only fire when enabled (e.g. at least one record exists in the transformation). */
  enabled: boolean;
  /**
   * Restrict the forecast to a window of records (#6755).
   *
   * Omitted means the whole document, which is the original behaviour. Supply a
   * window on a large document: the whole-document form loads every record
   * inside mgmt-api and OOM-kills it at scale (#6826), so without this the
   * review tab could not show forecasts at all — and the forecast layer is the
   * most useful thing on the screen.
   *
   * ONLY SAFE when no claimant straddles the window edge, since a claimant's
   * ytd_delta sums paid_amount across all of its rows. Gate on
   * record_summary.max_rows_per_claimant === 1 before passing this.
   */
  window?: { fromRecordIndex: number; limit: number };
}

/** Query string for the GET form: client first, then the optional window (#6755). */
export const buildReviewProjectionQuery = (
  clientId: string,
  window?: { fromRecordIndex: number; limit: number }
): string => {
  const params = new URLSearchParams({ client_id_ref: clientId });
  if (window) {
    params.set("from_record_index", String(window.fromRecordIndex));
    params.set("limit", String(window.limit));
  }
  return params.toString();
};

interface UseReviewProjectionReturn {
  projection: DocumentReviewProjection | null;
  isLoading: boolean;
  error: string | null;
  /** Force a refetch — e.g. after the reviewer changes client or re-processes the doc. */
  refetch: () => void;
}

/**
 * Fetches the per-claimant forecast from GET /documents/{id}/review-projection.
 *
 * GET, not POST, since #7433: the permission middleware maps GET-on-a-resource
 * to documents:show, so a viewer without documents:create gets the forecast
 * layer (On approve / Deductible / YTD) the same as a reviewer. The POST form
 * still exists for compatibility but maps to documents:create — calling it
 * here is what left viewers with no forecasts at all.
 *
 * Calls httpClient directly rather than dataProvider.getOne because the
 * response is not an identified record. httpClient still carries auth +
 * partner headers.
 *
 * Re-fetches whenever documentId or clientId changes. The endpoint is
 * non-destructive — calling it multiple times is safe.
 */
export const useReviewProjection = ({
  documentId,
  clientId,
  enabled,
  window,
}: UseReviewProjectionProps): UseReviewProjectionReturn => {
  const [projection, setProjection] = useState<DocumentReviewProjection | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);

  const refetch = useCallback(() => setRefreshKey((k) => k + 1), []);

  useEffect(() => {
    if (!enabled || !documentId || !clientId) {
      setProjection(null);
      setError(null);
      return;
    }

    let cancelled = false;
    setIsLoading(true);
    setError(null);

    const url = `${getPartnerAwareApiUrl()}/documents/${documentId}/review-projection?${buildReviewProjectionQuery(clientId, window)}`;
    httpClient(url, { method: "GET" })
      .then(({ json }: { json: unknown }) => {
        if (cancelled) return;
        const payload = json as Partial<DocumentReviewProjection> | undefined;
        if (!payload || !Array.isArray(payload.claimants)) {
          // Dev hint stays in the console; user-facing alert gets only the short reason.
          console.error(
            "Projection endpoint returned an unexpected response shape. " +
              "Is the mgmt-api running with story/4261 and the /review-projection route registered?",
            json
          );
          setError("Projection endpoint returned an unexpected response shape.");
          setProjection(null);
          return;
        }
        setProjection(payload as DocumentReviewProjection);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        console.error("Review projection failed:", err);
        // NOT err.message. react-admin's fetchJson builds that from
        // `body.message` (lowercase) and falls back to the HTTP status text,
        // while our error envelopes use `Message` — so every failure rendered
        // as "Conflict" and the server's code never reached the reviewer
        // (#6826). projectionErrorReason reads the envelope.
        setError(projectionErrorReason(err));
        setProjection(null);
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
    // Depend on the window's VALUES, not the object — the caller builds it
    // inline, so an object identity dep would refetch on every render.
  }, [documentId, clientId, enabled, refreshKey, window?.fromRecordIndex, window?.limit]);

  return { projection, isLoading, error, refetch };
};
